import { t } from '@hefe/i18n';
import {
  Club,
  ResponseStripeGetBillingPortal,
  StripePrice,
  StripeProduct,
  StripeProductLimits,
} from '@hefe/models';
import { API, ModalsSlice, StripeApi, currentUserSelector } from '@hefe/store';
import { dateFormat, formatDate } from '@hefe/utils';
import {
  Button,
  Col,
  Descriptions,
  Popconfirm,
  Row,
  Space,
  Spin,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';
import ClubSubscriptionCheckUpdateComponent from '../club-subscription-check-update/club-subscription-check-update.component';
import ClubSubscriptionProductCardComponent from '../club-subscription-product-card/club-subscription-product-card.component';
import { environment } from './../../../../../environments/environment';
import { colors } from '@hefe/ui';

interface ClubSubscriptionsComponentProps {
  club: Club;
}

export default function ClubSubscriptionsComponent(
  props: ClubSubscriptionsComponentProps
) {
  const { club } = props;

  const dispatch = useAppDispatch();
  const user = useSelector(currentUserSelector);

  const [loadingBillingPortal, setLoadingBillingPortal] = useState(false);

  const { data: products, isLoading: isLoadingProducts } =
    StripeApi.useFetchProductsQuery();

  const [cancelDowngradeSubscription, { isLoading: isCancelingDowngrade }] =
    StripeApi.useCancelDowngradeSubscriptionMutation();

  const openBillingPortal = () => {
    setLoadingBillingPortal(true);
    API.stripe()
      .getBillingPortal()
      .then((response) => {
        // open session.url in new tab
        const res = response?.data as ResponseStripeGetBillingPortal;
        if (res.status === 'success') {
          window.open(res.url, '_blank');
        }
      })
      .finally(() => setLoadingBillingPortal(false));
  };

  const openUpdateSubscriptionModal = (
    price: StripePrice,
    product: StripeProduct
  ) => {
    const type = t(
      'subscriptions.change_subscription.button_type.' + price.update_type
    );
    const priceAmount = (price.unit_amount / 100).toFixed(2);
    const priceText = t(
      `subscriptions.price.per_${price.type === 'year' ? 'year' : 'month'}`,
      { amount: priceAmount }
    );
    const title = `${type}: ${product.name} (${priceText})`;

    dispatch(
      ModalsSlice.modalsActions.openModal({
        title: title,
        width: '80%',
        hideOkButton: true,
        hideCancelButton: true,
        component: (
          <ClubSubscriptionCheckUpdateComponent
            price={price}
            product={product}
          />
        ),
      })
    );
  };

  const cancelDowngrade = () => {
    cancelDowngradeSubscription({}).then(() => {
      window.location.reload();
    });
  };

  const showSubscription = (limits?: StripeProductLimits) => {
    if (!limits) {
      return false;
    }

    const usersLimit = limits?.users ?? 0;
    const basketballTeamsLimit = limits?.basketball_teams ?? 0;
    const tasksLimit = limits?.tasks ?? 0;

    return (
      usersLimit >= (club.statistics?.user_count ?? 0) &&
      basketballTeamsLimit >= (club.statistics?.basketball_teams_count ?? 0) &&
      tasksLimit >= (club.statistics?.tasks_count ?? 0)
    );
  };

  const renderContent = () => {
    if (club?.subscriptions && club?.subscriptions?.length > 0) {
      return renderSubscriptionContent();
    } else {
      return renderNoSubscriptionContent();
    }
  };

  const renderChangeSubscription = () => {
    if (club.subscription_schedule) {
      return;
    }

    const allProducts = products?.filter((product) => product.id !== '');

    const productDescriptionItems = allProducts?.map((product) => {
      const offsets = Object.keys(product?.offsets ?? {}).map((offsetKey) => {
        const offset = ((product?.offsets as any)[offsetKey] as number) ?? 0;

        let offsetType = '';
        switch (offsetKey) {
          case 'users':
            offsetType = t('users.users');
            break;
          case 'basketball_teams':
            offsetType = t('teams');
            break;
          case 'tasks':
            offsetType = t('tasks.plural');
            break;
        }

        return (
          <Space direction="vertical" size={0} key={offsetKey}>
            <Typography.Text>
              {t('subscriptions.change_subscription.too_many', {
                type: offsetType,
                count: offset,
              })}
            </Typography.Text>
          </Space>
        );
      });

      if (!offsets || offsets.length === 0) {
        const pricesOutput = [];

        for (const price of product.prices) {
          if (price.update_type === 'not_allowed') {
            continue;
          }

          pricesOutput.push(
            <Space key={price.id}>
              <Typography.Text strong style={{ fontSize: 16 }}>
                {t(
                  'subscriptions.price.' +
                    (price.type === 'year' ? 'per_year' : 'per_month'),
                  { amount: (price.unit_amount / 100).toFixed(2) }
                )}
              </Typography.Text>
              <Button
                disabled={price.update_type === 'active'}
                onClick={() => openUpdateSubscriptionModal(price, product)}
              >
                {t(
                  `subscriptions.change_subscription.button_type.${price.update_type}`
                )}
              </Button>
            </Space>
          );
        }

        offsets.push(
          <Space direction="vertical" size={16} key={product.id}>
            {pricesOutput}
          </Space>
        );
      }

      return (
        <Descriptions.Item key={product.id} label={product.name}>
          <Space direction="vertical" size={0}>
            {offsets}
          </Space>
        </Descriptions.Item>
      );
    });

    return (
      <Space direction="vertical" size={12}>
        <Typography.Paragraph>
          {t('subscriptions.change_subscription.info_with_restrictions')}
        </Typography.Paragraph>
        <Descriptions bordered column={1}>
          {productDescriptionItems}
        </Descriptions>
      </Space>
    );
  };

  const renderSubscriptionContent = () => {
    return (
      <Space direction="vertical" size={12}>
        <Descriptions bordered column={1}>
          <Descriptions.Item label={t('subscriptions.billing_portal')}>
            <Button
              type="link"
              onClick={() => openBillingPortal()}
              loading={loadingBillingPortal}
            >
              {t('subscriptions.billing_portal')} &nbsp;
              <FaExternalLinkAlt />
            </Button>
          </Descriptions.Item>
          {club?.trial_ends_at && dayjs().isBefore(dayjs(club?.trial_ends_at)) && (
            <Descriptions.Item label={t('subscriptions.trial.title')}>
              <Typography.Text>
                {t('subscriptions.trial.info', {
                  date: formatDate(club?.trial_ends_at),
                })}
              </Typography.Text>
            </Descriptions.Item>
          )}
          <Descriptions.Item label={t('subscriptions.active_subscriptions')}>
            {club?.subscriptions?.map((subscription) => (
              <Space direction="vertical" size={0} key={subscription.name}>
                <Typography.Text strong>{subscription?.name}</Typography.Text>
                <Typography.Text>
                  {formatDate(subscription?.start)} -{' '}
                  {formatDate(subscription?.end)}
                </Typography.Text>
                <Typography.Text>
                  {t(
                    'subscriptions.price.' +
                      (subscription.interval === 'year'
                        ? 'per_year'
                        : 'per_month'),
                    { amount: subscription.price }
                  )}
                </Typography.Text>
              </Space>
            ))}
          </Descriptions.Item>
          {club?.subscription_schedule && (
            <Descriptions.Item
              label={t(
                'subscriptions.change_subscription.downgrade.pending_title'
              )}
            >
              <Space direction="vertical" size={0}>
                <Typography.Text strong>
                  {club?.subscription_schedule?.name}
                </Typography.Text>
                <Typography.Text>
                  {formatDate(club?.subscription_schedule?.start)} -{' '}
                  {formatDate(club?.subscription_schedule?.end)}
                </Typography.Text>
                <Typography.Text>
                  {t(
                    'subscriptions.price.' +
                      (club?.subscription_schedule.interval === 'year'
                        ? 'per_year'
                        : 'per_month'),
                    { amount: club?.subscription_schedule.price }
                  )}
                </Typography.Text>
                <Typography.Text>
                  {t('subscriptions.change_subscription.downgrade.info', {
                    date: dayjs(club?.subscription_schedule?.start)
                      .subtract(1, 'day')
                      .format(dateFormat()),
                  })}
                </Typography.Text>
                <Popconfirm
                  disabled={isCancelingDowngrade}
                  title={t(
                    'subscriptions.change_subscription.downgrade.button_tooltip'
                  )}
                  onConfirm={() => cancelDowngrade()}
                >
                  <Button
                    loading={isCancelingDowngrade}
                    danger
                    type="primary"
                    style={{ marginTop: 10 }}
                  >
                    {t('subscriptions.change_subscription.downgrade.button')}
                  </Button>
                </Popconfirm>
              </Space>
            </Descriptions.Item>
          )}
        </Descriptions>
        {renderChangeSubscription()}
      </Space>
    );
  };

  const renderNoSubscriptionContent = () => {
    if (isLoadingProducts) {
      return <Spin />;
    }

    return (
      <Space direction="vertical" size={12}>
        {club?.trial_ends_at && dayjs().isBefore(dayjs(club?.trial_ends_at)) && (
          <Typography.Text strong style={{ color: colors.primary }}>
            {t('subscriptions.trial.info', {
              date: formatDate(club?.trial_ends_at),
            })}
          </Typography.Text>
        )}
        <Typography.Text>
          {t('subscriptions.no_subscription.info')}
        </Typography.Text>
        <Space>
          <Typography.Text>
            {t('subscriptions.full_features.info')}
          </Typography.Text>
          <Button
            type="link"
            href={environment.subscriptionsUrl}
            target="_blank"
          >
            {t('subscriptions.full_features.link')}
          </Button>
        </Space>
        <Row gutter={16} justify="space-evenly">
          {products
            ?.filter(
              (product) =>
                product.id !== '' && showSubscription(product?.limits)
            )
            .sort(
              (a, b) => a?.prices[0]?.unit_amount - b?.prices[0]?.unit_amount
            )
            .map((product) => (
              <Col key={product.id} md={8} sm={24}>
                <ClubSubscriptionProductCardComponent product={product} />
              </Col>
            ))}
        </Row>
      </Space>
    );
  };

  return renderContent();
}
